import React, { useState } from 'react'
import { CardFrame } from '../../../ui/CardFrame/CardFrame.jsx'
import { MiniPill } from '../../../ui/MiniPill/MiniPill.jsx'
import { PullBanner } from '../../../ui/PullCarryBanner/PullCarryBanner.jsx'
import { IconButton } from '../../../ui'

/**
 * TalkingPointsCard — session-talking-points (catalog #1, CORE).
 *
 * Renders the talking-point checklist with PullBanner at the top showing
 * the count of prep bullets pulled into the session. Pulled rows show a
 * source MiniPill (⇣ from <PrepCard.kind>).
 *
 * Empty-state copy (locked in design-takeover-copy-decisions.md §4):
 * "No talking points yet. Start the session — items from your prep will
 * appear here as the session runs."
 *
 * Pull affordance: lists the report's prep bullets; clicking a bullet
 * calls pullFromPrep(bulletId).
 *
 * Phase F: PullBanner count = pullEdges.length for this session.
 */

const PREP_KIND_LABELS = {
  'prep-risks': 'Risks',
  'prep-asks': 'Asks',
  'prep-wins': 'Wins',
  'prep-learnings': 'Learnings',
  'prep-focus': 'Focus areas',
  'prep-priorities': 'Priorities',
  'prep-stuck': 'Stuck on',
  'prep-feedback': 'Feedback',
  'prep-support': 'Support',
  'prep-progress-updates': 'Progress',
}

function kindLabel(kind) {
  return PREP_KIND_LABELS[kind] ?? kind ?? 'prep'
}

export function TalkingPointsCard({
  talkingPoints = [],
  pullEdges = [],
  prepBullets = [],
  onUpdateStatus,
  onAdd,
  onRemove,
  onPullFromPrep,
  disabled = false,
}) {
  const [showPullPanel, setShowPullPanel] = useState(false)
  const pullCount = pullEdges.length
  const hasItems = talkingPoints.length > 0

  return (
    <CardFrame
      kind="session"
      icon="◎"
      iconClass="clay"
      title="Talking points"
      prompt="What do you want to cover today?"
      state={disabled ? 'locked' : hasItems ? 'filled' : 'empty'}
      data-testid="session-card-talking-points"
      className="session-card"
    >
      {pullCount > 0 && (
        <PullBanner
          count={pullCount}
          source="from this morning's prep"
          className="session-card__pull-banner"
        />
      )}

      {hasItems ? (
        <div className="tp-list">
          {talkingPoints.map((tp) => {
            const discussed = tp.status === 'discussed'
            const sourceKind = tp.sourcePrepCardKind
            return (
              <div key={tp.talkingPointId} className="tp-list__row">
                <button
                  type="button"
                  className={`tp-list__circle ${discussed ? 'tp-list__circle--filled' : ''}`}
                  onClick={() => !disabled && onUpdateStatus?.(tp.talkingPointId, discussed ? 'to-discuss' : 'discussed')}
                  disabled={disabled}
                  aria-label={discussed ? 'Mark not discussed' : 'Mark discussed'}
                  aria-pressed={discussed}
                />
                <span
                  className="tp-list__text"
                  style={{ textDecoration: discussed ? 'line-through' : 'none', color: discussed ? 'var(--color-text-faint)' : 'inherit' }}
                >
                  {tp.text}
                </span>
                {tp.status === 'to-discuss' && (
                  <MiniPill kind="to-discuss" aria-label="To discuss">to discuss</MiniPill>
                )}
                {discussed && (
                  <MiniPill kind="discussed" aria-label="Discussed">discussed</MiniPill>
                )}
                {sourceKind && (
                  <MiniPill kind="to-discuss" aria-label={`From ${kindLabel(sourceKind)}`}>
                    ⇣ from {kindLabel(sourceKind)}
                  </MiniPill>
                )}
                {!disabled && (
                  <IconButton
                    ariaLabel="Remove talking point"
                    onClick={() => onRemove?.(tp.talkingPointId)}
                    tabIndex={-1}
                  >×</IconButton>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <p className="session-card__empty">
          No talking points yet. Start the session — items from your prep will appear here as the session runs.
        </p>
      )}

      {!disabled && (
        <div className="tp-list__footer">
          <button
            type="button"
            className="tp-list__add-btn"
            onClick={() => onAdd?.('')}
          >
            + add
          </button>
          <span className="tp-list__divider" aria-hidden="true">|</span>
          {onPullFromPrep && prepBullets.length > 0 ? (
            <button
              type="button"
              className="tp-list__pull-btn"
              onClick={() => setShowPullPanel((v) => !v)}
              aria-expanded={showPullPanel}
              aria-controls="tp-pull-panel"
            >
              pull from prep
            </button>
          ) : null}
        </div>
      )}

      {showPullPanel && prepBullets.length > 0 && (
        <div id="tp-pull-panel" className="tp-pull-panel" role="list" aria-label="Prep bullets available to pull">
          {prepBullets.map((bullet) => (
            <button
              key={bullet.bulletId}
              type="button"
              className="tp-pull-panel__item"
              role="listitem"
              onClick={() => {
                onPullFromPrep?.(bullet.bulletId, bullet.text)
                setShowPullPanel(false)
              }}
            >
              <span className="tp-pull-panel__source">
                {bullet.cardKind ? kindLabel(bullet.cardKind) : 'prep'}
              </span>
              <span className="tp-pull-panel__text">{bullet.text}</span>
            </button>
          ))}
        </div>
      )}
    </CardFrame>
  )
}
